import type { CapturePolicy } from '@kyc/shared';

/**
 * Camera acquisition — §13 resolution/FPS fallback ladder. Only the policy's accepted
 * portrait resolutions are usable; anything else routes to the unsupported path (the
 * backend would reject it anyway, §1/§22).
 */

export interface CameraSession {
  stream: MediaStream;
  track: MediaStreamTrack;
  width: number;
  height: number;
  quality: 'FHD' | 'HD';
}

export type CameraKind = 'rear' | 'front';

export class CameraUnsupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CameraUnsupportedError';
  }
}

export class CameraPermissionError extends Error {
  constructor() {
    super('Camera permission was denied. Allow camera access in your browser settings and refresh the page.');
    this.name = 'CameraPermissionError';
  }
}

function videoConstraints(kind: CameraKind, width: number, height: number, exact: boolean): MediaStreamConstraints {
  const size = exact
    ? { width: { exact: width }, height: { exact: height } }
    : { width: { ideal: width }, height: { ideal: height } };
  return {
    audio: false,
    video: {
      facingMode: kind === 'rear' ? { ideal: 'environment' } : { ideal: 'user' },
      ...size,
      frameRate: { ideal: 30, max: 30 },
      // §1 — digital zoom stays at 1.0×; no filters.
      advanced: [{ zoom: 1.0 } as MediaTrackConstraintSet],
    },
  };
}

async function tryOpen(kind: CameraKind, width: number, height: number, exact: boolean): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getUserMedia(videoConstraints(kind, width, height, exact));
  } catch (err) {
    // Permission problems are terminal — retrying other resolutions just re-prompts.
    if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
      throw new CameraPermissionError();
    }
    return null;
  }
}

export async function openCamera(kind: CameraKind, policy: CapturePolicy): Promise<CameraSession> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CameraUnsupportedError('Camera API unavailable in this browser');
  }
  // §13 — FHD first, then HD; never below the minimum quality. Exact constraints first;
  // some browsers reject exact portrait sizes but deliver them via ideal, so each rung
  // gets a second ideal-based attempt whose actual settings are then strictly verified.
  for (const res of policy.mediaPolicy.acceptedDisplayedResolutions) {
    for (const exact of [true, false]) {
      const stream = await tryOpen(kind, res.width, res.height, exact);
      if (!stream) continue;
      const track = stream.getVideoTracks()[0];
      if (!track) {
        stream.getTracks().forEach((t) => t.stop());
        continue;
      }
      const settings = track.getSettings();
      const ok = settings.width === res.width && settings.height === res.height;
      if (!ok) {
        stream.getTracks().forEach((t) => t.stop());
        continue;
      }
      return {
        stream,
        track,
        width: res.width,
        height: res.height,
        quality: res.width >= 1080 ? 'FHD' : 'HD',
      };
    }
  }
  throw new CameraUnsupportedError(
    'This device does not support the required portrait resolution. Please use a mobile phone.',
  );
}

export function closeCamera(session: CameraSession | null): void {
  session?.stream.getTracks().forEach((t) => t.stop());
}
